import React, { useState, useCallback } from 'react';
import {
    Box,
    Input,
    Button,
    Text,
    Flex,
    Heading,
    Grid,
    useBreakpointValue,
    useTheme,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import { HotspotFilterState } from '../types/hotspots';
import { getTodayDateString } from '../utils/hotspots';
import HotspotsStats from '../hotspots/HotspotsStats';

interface FilterHotspotsProps {
    filters: HotspotFilterState;
    onFilterChange: (filters: HotspotFilterState) => void;
    onClearFilters: () => void;
    onApplyFilters: () => void;
    hotspots?: any[];
    loading?: boolean;
    error?: string | null;
}

const FilterHotspots: React.FC<FilterHotspotsProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
    hotspots,
    loading,
    error,
}) => {
    const theme = useTheme();
    const [localFilters, setLocalFilters] = useState<HotspotFilterState>(filters);
    const footerGap = useBreakpointValue({ base: 2, md: 3 });

    // Style constants matching Filter.tsx
    const inputStyles = {
        size: "sm" as const,
        borderRadius: "lg",
        borderColor: "gray.200",
        _hover: { borderColor: "gray.300" },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    const handleInputChange = useCallback((field: keyof HotspotFilterState, value: string) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleSeverityChange = useCallback((severityLabels: string[]) => {
        const severityString = severityLabels.join(',');
        const newFilters = { ...localFilters, severity: severityString };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleApplyFilters = useCallback(() => {
        onFilterChange(localFilters);
        onApplyFilters();
    }, [localFilters, onFilterChange, onApplyFilters]);

    const handleClearFilters = useCallback(() => {
        const clearedFilters = {
            start_date: getTodayDateString(),
            end_date: getTodayDateString(),
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    }, [onFilterChange, onClearFilters]);

    const selectedSeverities = localFilters.severity ? localFilters.severity.split(',') : [];

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            bg="white"
            // overflow="hidden"
        >
            {/* Header Section - Fixed */}
            <Flex
                as="header"
                position="sticky"
                top={0}
                bg="white"
                zIndex={2}
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 2, md: 0 })}
                align="center"
                minHeight={useBreakpointValue({ base: "60px", md: "72px" })}
            >
                <Heading size={useBreakpointValue({ base: "sm", md: "md" })}>
                    Titik Panas
                </Heading>
            </Flex>

            {/* Scrollable Content */}
            <Box
                flex={1}
                overflowY="auto"
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 3, md: 4 })}
                position="relative"
            >
                <Grid templateColumns="1fr" gap={useBreakpointValue({ base: 3, md: 4 })}>
                    {/* Date Range */}
                    <Flex gap={useBreakpointValue({ base: 2, md: 3 })} direction={{ base: "column", sm: "row" }}>
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                                Dari Tanggal
                            </Text>
                            <Input
                                type="date"
                                value={localFilters.start_date || getTodayDateString()}
                                onChange={(e) => handleInputChange('start_date', e.target.value)}
                                placeholder="YYYY-MM-DD"
                                title="Dari tanggal (format: YYYY-MM-DD)"
                                {...inputStyles}
                                width="full"
                            />
                        </Box>
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                                Sampai Tanggal
                            </Text>
                            <Input
                                type="date"
                                value={localFilters.end_date || getTodayDateString()}
                                onChange={(e) => handleInputChange('end_date', e.target.value)}
                                {...inputStyles}
                                width="full"
                            />
                        </Box>
                    </Flex>

                    {/* Stats */}
                    <Box width="full">
                        <Box 
                            p={useBreakpointValue({ base: 3, md: 3 })}
                            bg="gray.50"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <HotspotsStats 
                                isSatellite={false} 
                                hotspots={hotspots}
                                loading={loading}
                                error={error}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Box>

            {/* Fixed Footer */}
            <Flex
                bg="white"
                zIndex={3}
                px="16px"
                pt="16px"
                justify="flex-end"
                gap={footerGap}
                flexShrink={0}
                sx={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
            >
                <Button
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    onClick={handleClearFilters}
                    leftIcon={<MdClear />}
                    width={{ base: "full", sm: "auto" }}
                >
                    Hapus Filter
                </Button>

                <Button
                    colorScheme="green"
                    size="sm"
                    onClick={handleApplyFilters}
                    leftIcon={<FiSearch />}
                    width={{ base: "full", sm: "auto" }}
                >
                    Terapkan Filter
                </Button>
            </Flex>
        </Box>
    );
};

export default FilterHotspots;